import { useState } from "react";

import { DEVICE_ACTIONS, DialogProps } from "components/Actions/types";
import { FILE_ID_TYPE, TRI_SOURCES, usePermissions } from "shared";

import DialogActions from "../../components/DialogActions";
import TriSource from "../../components/TriSource/TriSource";
import CompanyAndGroupDropdowns from "../components/CompanyAndGroupDropdowns";
import DeviceRadioGroup from "../components/DeviceRadioGroup";
import { useTriSourceAction } from "../hooks";
import { CompanyGroupPayloadProps } from "../types";
import ClearTasksAndHistory from "./ClearTasksAndHistory";

const MoveDevices = ({ isOpen, close }: DialogProps) => {
    const { isReadDeviceGroupNotAllowed } = usePermissions();

    const {
        t,
        rootCompany,
        isSupportUser,
        activeSource,
        sourcePayload,
        isSourceReady,
        isSelectedSingleItem,
        updateDevices
    } = useTriSourceAction();

    const [isLoading, setIsLoading] = useState(false);
    const [deviceIdType, setDeviceIdType] = useState(FILE_ID_TYPE.Imei);

    const [companyGroupData, setCompanyGroupData] =
        useState<CompanyGroupPayloadProps | null>(null);

    const [deleteTasksAndHistory, setDeleteTasksAndHistory] = useState(false);

    // != intentionally: optional chaining yields undefined when companyGroupData is null, so a strict !== null check would incorrectly pass
    const isReady =
        isSourceReady &&
        sourcePayload !== null &&
        companyGroupData?.company != null;

    const handleCloseDialog = () => close(DEVICE_ACTIONS.MoveDevices);

    const handleCompanyGroup = (data: CompanyGroupPayloadProps | null) =>
        setCompanyGroupData(data);

    const getFormData = (
        company: number,
        group: number | null,
        deleteTasksAndHistory: boolean
    ) => {
        if (isReady) {
            const formData = new FormData();

            formData.append("source", activeSource);
            formData.append("id_type", deviceIdType);
            formData.append("file", sourcePayload as Blob);

            isSupportUser &&
                formData.append("root_id", String(rootCompany?.id));

            formData.append("data[company_id]", String(company));
            formData.append("data[group_id]", String(group));

            formData.append(
                "data[request_options][delete_tasks_and_history]",
                String(deleteTasksAndHistory)
            );

            return formData;
        }

        return null;
    };

    const getPayloadData = (
        company: number,
        group: number | null,
        deleteTasksAndHistory: boolean
    ) => {
        if (isReady) {
            const data = {
                company_id: company,
                group_id: group,
                request_options: {
                    delete_tasks_and_history: deleteTasksAndHistory
                }
            };

            const payload = {
                source: activeSource,
                [activeSource]: sourcePayload,
                data
            };

            return isSelectedSingleItem ? data : payload;
        }
    };

    const submitMoveDevices = () => {
        if (isReady) {
            const { company, group } = companyGroupData;

            const payloadData =
                activeSource === TRI_SOURCES.FromFile
                    ? getFormData(company!, group, deleteTasksAndHistory)
                    : getPayloadData(company!, group, deleteTasksAndHistory);

            updateDevices(payloadData, setIsLoading, handleCloseDialog);
        }
    };

    const handleDeviceIdChange = (e: { target: { value: FILE_ID_TYPE } }) => {
        const { value } = e.target;
        setDeviceIdType(value);
    };

    const isBulkActionLoading = !isSelectedSingleItem && isLoading;

    return (
        <TriSource
            title={t("Dialog##move devices")}
            description={t("Dialog##move device description")}
            confirmationDescription={t(
                "Dialog##confirmations##move devices confirmation"
            )}
            close={handleCloseDialog}
            isBulkActionLoading={isBulkActionLoading}
            isLoading={isLoading}
            open={isOpen}
            submit={submitMoveDevices}
            actions={
                <DialogActions
                    onClose={handleCloseDialog}
                    isLoading={isBulkActionLoading || isLoading}
                    loaderText={
                        !isSelectedSingleItem ? t("Button##moving") : undefined
                    }
                    isReadyToConfirm={isReady}
                    confirmationButtonText={t("Button##move")}
                    cancelButtonTestId="cancel-move-devices"
                    confirmationButtonTestId="move-devices"
                />
            }
            slotProps={{
                transition: {
                    onExited: () => {
                        setIsLoading(false);
                        setCompanyGroupData(null);
                        setDeleteTasksAndHistory(false);
                    }
                }
            }}
            deviceIdentifier={
                <DeviceRadioGroup
                    title={t("Dialog##device identifier")}
                    target={deviceIdType}
                    firstRadioLabel={t("Dialog##imei")}
                    firstRadioValue={FILE_ID_TYPE.Imei}
                    secondRadioLabel={t("Dialog##sn")}
                    secondRadioValue={FILE_ID_TYPE.SerialId}
                    isLoading={isLoading}
                    onChange={handleDeviceIdChange}
                />
            }
        >
            <CompanyAndGroupDropdowns
                isLoading={isLoading}
                isCompanyRequired
                isGroupNotAllowed={isReadDeviceGroupNotAllowed}
                clearData={
                    <ClearTasksAndHistory
                        isChecked={deleteTasksAndHistory}
                        onChange={(value: boolean) =>
                            setDeleteTasksAndHistory(value)
                        }
                    />
                }
                setPayload={handleCompanyGroup}
            />
        </TriSource>
    );
};

export default MoveDevices;
